import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import { Points } from 'components/Points/Points';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import { useCalculatePriceMutation } from 'store/services/trade/tradePriceApi';
import { useEstimatePriceMutation, useLazyGetCardByIdQuery } from 'store/services/cards/search/cardsSearchApi';
import { SetDetail } from 'store/services/trade/tradeApi';
import { AssetUrls } from 'config/assets';
import { FoilCardImage } from 'components/FoilCardImage';
import './InitiateTradeModal.css';

export interface TradeCard {
  id: string;
  qty: number;
  maxQty: number;  // Maximum quantity the receiver wants
  name: string;
  set: string;  // Current set name (for display)
  setCode: string;  // Current set code (for API calls)
  condition: string;
  language: string;
  foil: boolean;
  edition: string;
  image: string;
  imageSmall?: string;
  value: number;
  baseValue: number;  // Original value before modifications
  foilValue?: number;
  nonFoilValue?: number;
  acceptableSets?: string[];  // Set names the receiver will accept (for display)
  acceptableSetCodes?: string[];  // Set codes the receiver will accept (for API)
  acceptableSetDetails?: SetDetail[];  // Combined set info with enhanced data
  wantListId?: number;  // Added for API calls
  availableInFoil?: boolean;  // Whether this card is available in foil
  availableInNonFoil?: boolean;  // Whether this card is available in non-foil
  acceptFoil?: boolean;  // Whether the receiver accepts foil
  acceptNonFoil?: boolean;  // Whether the receiver accepts non-foil
  cardId?: number;  // Track selected card ID for specific version
  scryfallId?: string;  // Scryfall UUID for API calls (estimate-price, card detail)
  availableLanguages?: Array<{ lang: string; scryfallId: string; setCode?: string }>;  // Language variants for this printing
  acceptableLanguages?: string[];  // Languages the receiver accepts (from want record)
  acceptableConditions?: string[];  // Conditions the receiver accepts
  sourceCardId?: string;  // Original card.id from the package (for SendPage2 lookup)
}

interface Props {
  isOpen: boolean;
  cards: TradeCard[];
  onClose: () => void;
  onInitiateTrade: (cards: TradeCard[]) => void;
  isLoading?: boolean;
  lockId?: string;
  lockExpiresAt?: string;
  receiverUsername?: string;
  receiverAvatarUrl?: string;
  receiverPoints?: number;
  receiverCountry?: string;
  haveListQuantityMap?: Map<string, number>;
}

export default function InitiateTradeModal({ isOpen, cards, onClose, onInitiateTrade, isLoading = false, lockId, lockExpiresAt, receiverUsername, receiverAvatarUrl, receiverPoints, receiverCountry, haveListQuantityMap }: Props) {
  const [editableCards, setEditableCards] = useState<TradeCard[]>(cards);
  const [timeRemaining, setTimeRemaining] = useState<number>(300); // 5 minutes in seconds
  const [calculatePrice, { isLoading: isCalculatingPrice }] = useCalculatePriceMutation();
  const [estimatePrice] = useEstimatePriceMutation();
  const [triggerGetCard] = useLazyGetCardByIdQuery();
  const isInitializedRef = useRef(false);

  // Reset initialization flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      isInitializedRef.current = false;
    }
  }, [isOpen]);

  // Initialize cards when modal first opens - expand each card into individual per-row entries
  // Then recalculate prices to get correct condition-adjusted values from backend
  // Guard: only run on first open, not on subsequent prop changes from remote data updates
  useEffect(() => {
    if (isOpen && cards.length > 0 && !isInitializedRef.current) {
      isInitializedRef.current = true;
      const expandedRows: TradeCard[] = [];
      cards.forEach(card => {
        for (let i = 0; i < card.qty; i++) {
          expandedRows.push({
            ...card,
            id: `${card.wantListId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            sourceCardId: card.id,
            qty: 1,
          });
        }
      });
      setEditableCards(expandedRows);

      // Recalculate prices so they reflect the actual condition (not just NM defaults)
      const recalculateInitialPrices = async () => {
        const cardsWithWantListId = expandedRows.filter(card => card.wantListId);
        if (cardsWithWantListId.length === 0) return;

        try {
          const result = await calculatePrice({
            cards: cardsWithWantListId.map(card => ({
              wantListId: card.wantListId!,
              cardId: card.cardId?.toString(),
              condition: card.condition,
              language: card.language,
              foil: card.foil,
              set: card.set
            }))
          }).unwrap();

          // Map prices by index — expanded rows share wantListId, so use 1:1 index correspondence
          setEditableCards(prev =>
            prev.map(card => {
              const reqIndex = cardsWithWantListId.findIndex(c => c.id === card.id);
              if (reqIndex !== -1 && reqIndex < result.cards.length) {
                return { ...card, value: result.cards[reqIndex].displayPrice };
              }
              return card;
            })
          );
        } catch (error) {
          console.error('Failed to recalculate initial prices:', error);
        }
      };
      recalculateInitialPrices();
    }
  }, [isOpen, cards, calculatePrice]);

  // Initialize timer only when modal opens (not on every render)
  useEffect(() => {
    if (isOpen) {
      // Only reset timer when modal is first opened (transition from closed to open)
      if (lockExpiresAt) {
        const expiresAt = new Date(lockExpiresAt).getTime();
        const now = Date.now();
        const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
        setTimeRemaining(remaining);
      } else {
        setTimeRemaining(300); // Default to 5 minutes
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only depend on isOpen, not lockExpiresAt which might change with polling

  // Countdown timer effect
  useEffect(() => {
    if (!isOpen || timeRemaining <= 0) return;

    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          onClose(); // Auto-close when timer expires
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, timeRemaining, onClose]);

  // Format time for display
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Compute total quantity per card name in the package (for "You Own" warning comparison)
  const packageQtyByName = useMemo(() => {
    const map = new Map<string, number>();
    editableCards.forEach(card => {
      map.set(card.name, (map.get(card.name) || 0) + 1);
    });
    return map;
  }, [editableCards]);

  // Group rows by wantListId for visual grouping (must be before early return)
  const groupedCards = useMemo(() => {
    const groups: Record<number, TradeCard[]> = {};
    const groupOrder: number[] = [];
    editableCards.forEach(card => {
      const key = card.wantListId!;
      if (!groups[key]) {
        groups[key] = [];
        groupOrder.push(key);
      }
      groups[key].push(card);
    });
    return { groups, groupOrder };
  }, [editableCards]);

  if (!isOpen) return null;

  // Server-side price calculation - secure against manipulation
  const fetchCardPrices = async (cardsToPrice: TradeCard[]): Promise<Map<number, number>> => {
    const priceMap = new Map<number, number>();
    
    // Filter cards that have wantListId for API call
    const cardsWithWantListId = cardsToPrice.filter(card => card.wantListId);
    
    if (cardsWithWantListId.length === 0) {
      // If no cards have wantListId, return current values as fallback
      cardsToPrice.forEach(card => {
        if (card.wantListId) {
          priceMap.set(card.wantListId, card.value);
        }
      });
      return priceMap;
    }
    
    try {
      const requestPayload = {
        cards: cardsWithWantListId.map(card => ({
          wantListId: card.wantListId!,
          cardId: card.cardId?.toString(),  // Specific card ID for exact version matching
          condition: card.condition,
          language: card.language,
          foil: card.foil,
          set: card.set
        }))
      };
      
      const result = await calculatePrice(requestPayload).unwrap();

      result.cards.forEach(cardPrice => {
        priceMap.set(cardPrice.wantListId, cardPrice.displayPrice);
      });
    } catch (error) {
      console.error('Failed to fetch prices from server:', error);
      // On error, use existing display values
      cardsToPrice.forEach(card => {
        if (card.wantListId) {
          priceMap.set(card.wantListId, card.value);
        }
      });
    }
    
    return priceMap;
  };

  const updateCard = async (cardId: string, updates: Partial<TradeCard>) => {
    const card = editableCards.find(c => c.id === cardId);
    if (!card) return;
    
    // Validate foil changes against physical availability AND receiver's preferences
    if ('foil' in updates) {
      const requestedFoil = updates.foil;

      // Step 1: Physical availability (hard constraint)
      const physicallyAvailableInFoil = card.availableInFoil ?? false;
      const physicallyAvailableInNonFoil = card.availableInNonFoil ?? true;

      // Step 2: User preferences (undefined = accepts any)
      const userAcceptsFoil = card.acceptFoil ?? true;
      const userAcceptsNonFoil = card.acceptNonFoil ?? true;

      // Step 3: Intersect - must be BOTH physically available AND user accepts
      const canOfferFoil = physicallyAvailableInFoil && userAcceptsFoil;
      const canOfferNonFoil = physicallyAvailableInNonFoil && userAcceptsNonFoil;

      if (requestedFoil === true && !canOfferFoil) {
        toast.warning('This card is not available in foil');
        return;
      }
      if (requestedFoil === false && !canOfferNonFoil) {
        toast.warning('This card is not available in non-foil');
        return;
      }
    }
    
    const updatedCard = { ...card, ...updates };
    
    // If price-affecting fields changed, get new price from server
    // Set changes take priority — when set changes also resets language, use the set change pricing path
    if ('setCode' in updates && updatedCard.wantListId) {
      const priceMap = await fetchCardPrices([updatedCard]);
      const newPrice = priceMap.get(updatedCard.wantListId);
      if (newPrice !== undefined) {
        updatedCard.value = newPrice;
      }
    } else if ('language' in updates && updatedCard.availableLanguages) {
      // Language-only change (no set change)
      const langVariant = updatedCard.availableLanguages.find(
        (l) => l.lang === updatedCard.language
      );
      const estimateCardId = langVariant?.scryfallId || updatedCard.scryfallId;
      if (estimateCardId) {
        try {
          const result = await estimatePrice({
            cardId: estimateCardId,
            language: updatedCard.language,
            condition: updatedCard.condition,
            foil: updatedCard.foil,
          }).unwrap();
          const price = result.price ?? (result as any).data?.price;
          if (price != null) {
            updatedCard.value = price;
          }
        } catch {
          // Keep existing value on error
        }
      }
      // Update card image to match the selected language variant
      if (langVariant?.scryfallId) {
        updatedCard.image = AssetUrls.cards.getCardImage(langVariant.scryfallId, 'normal');
        updatedCard.imageSmall = AssetUrls.cards.getCardImage(langVariant.scryfallId, 'small');
      }
    } else if (('foil' in updates || 'condition' in updates) && updatedCard.wantListId) {
      const priceMap = await fetchCardPrices([updatedCard]);
      const newPrice = priceMap.get(updatedCard.wantListId);
      if (newPrice !== undefined) {
        updatedCard.value = newPrice;
      }
    }
    
    setEditableCards(prev => 
      prev.map(c => c.id === cardId ? updatedCard : c)
    );
  };


  const addRow = (wantListId: number) => {
    const templateRow = editableCards.find(c => c.wantListId === wantListId);
    if (!templateRow) return;

    const currentCount = editableCards.filter(c => c.wantListId === wantListId).length;
    if (currentCount >= templateRow.maxQty) {
      toast.warning(`Receiver only wants ${templateRow.maxQty} copies of ${templateRow.name}`);
      return;
    }

    const newRow: TradeCard = {
      ...templateRow,
      id: `${wantListId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      qty: 1,
    };

    setEditableCards(prev => {
      const lastIndex = prev.map(c => c.wantListId).lastIndexOf(wantListId);
      const result = [...prev];
      result.splice(lastIndex + 1, 0, newRow);
      return result;
    });
  };

  const removeRow = (rowId: string) => {
    setEditableCards(prev => prev.filter(c => c.id !== rowId));
  };

  // Display totals - each row is qty=1
  const totalValue = editableCards.reduce((sum, card) => sum + (card.value || 0), 0);
  const totalCards = editableCards.length;

  const handleInitiateTrade = () => {
    // All price validation happens server-side
    // Frontend prices are for display only
    onInitiateTrade(editableCards);
    // Don't call onClose() here - parent will close on success or handle errors
  };

  const openConditionGuide = () => {
    window.open('/articles/condition-guide', '_blank');
  };

  const openShippingGuide = () => {
    window.open('/articles/shipping', '_blank');
  };

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="initiate-trade-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Finalize Your Trade Package</h2>
          <div className="timer-display" style={{ 
            color: timeRemaining < 60 ? '#ff6b6b' : '#999',
            fontSize: '14px',
            fontWeight: 'bold',
            marginLeft: 'auto',
            marginRight: '10px'
          }}>
            Time remaining: {formatTime(timeRemaining)}
          </div>
          <button className="close-button" onClick={onClose}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        <div className="modal-content">
          <div className="package-summary">
            {receiverUsername && (
              <div className="receiver-info">
                <AvatarWithFlag
                  avatarUrl={receiverAvatarUrl}
                  username={receiverUsername}
                  size={40}
                  countryCode={receiverCountry}
                />
                <div className="receiver-details">
                  <span className="receiver-username">{receiverUsername}</span>
                  <Points points={receiverPoints} type="small" />
                </div>
              </div>
            )}
            <div className="summary-stats">
              <div className="summary-item">
                <span className="summary-label">Total Cards:</span>
                <span className="summary-value">{totalCards}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Total Value:</span>
                <span className="summary-value">{totalValue.toLocaleString()} points</span>
              </div>
            </div>
          </div>

          {receiverPoints !== undefined && totalValue > receiverPoints && (
            <div className="balance-warning">
              Receiver's balance ({receiverPoints.toLocaleString()} pts) may not cover this package ({totalValue.toLocaleString()} pts). You can still adjust and send.
            </div>
          )}

          <div className="guide-links">
            <button className="guide-link" onClick={openConditionGuide}>
              Condition Guide
            </button>
            <button className="guide-link" onClick={openShippingGuide}>
              Shipping Guide
            </button>
          </div>

          <div className="cards-table">
            <div className="cards-table-wrapper">
              <div className="table-header">
                <div className="col-name">Name</div>
                <div className="col-set">Set</div>
                <div className="col-foil">Foil</div>
                <div className="col-condition">Condition</div>
                <div className="col-language">Language</div>
                <div className="col-you-own">You Own</div>
                <div className="col-value">Value</div>
                <div className="col-remove"></div>
              </div>

              {groupedCards.groupOrder.map(wantListId => {
                const rows = groupedCards.groups[wantListId];
                if (!rows || rows.length === 0) return null;
                const firstCard = rows[0];

                return (
                  <div key={wantListId} className="card-group">
                    {rows.map((card, index) => (
                      <div key={card.id} className={`table-row ${index > 0 ? 'continuation-row' : ''}`}>
                        <div className="col-name">
                          {(card.imageSmall || card.image) ? (
                            <FoilCardImage
                              src={card.imageSmall || card.image}
                              alt={card.name}
                              className="card-thumbnail"
                              foil={card.foil}
                              borderRadius="4px"
                            />
                          ) : (
                            <div className="card-thumbnail card-thumbnail-placeholder" />
                          )}
                          <span>{card.name}</span>
                        </div>

                        <div className="col-set">
                          {(() => {
                            const hasMultipleSetDetails = card.acceptableSetDetails && card.acceptableSetDetails.length > 1;
                            const hasMultipleSets = !card.acceptableSetDetails && card.acceptableSets && card.acceptableSets.length > 1;
                            const shouldShowDropdown = hasMultipleSetDetails || hasMultipleSets;

                            const setOptions: SetDetail[] = card.acceptableSetDetails ||
                              (card.acceptableSets?.map((name, idx) => ({
                                name: name,
                                code: card.acceptableSetCodes?.[idx] || name,
                                cardId: 0,
                              })) || []);

                            if (shouldShowDropdown) {
                              const currentValue = card.cardId
                                ? `${card.setCode}-${card.cardId}`
                                : card.setCode;

                              return (
                                <select
                                  value={currentValue}
                                  onChange={async (e) => {
                                    const selectedValue = e.target.value;
                                    const selectedSetDetail = setOptions.find(s => {
                                      const optionValue = s.cardId
                                        ? `${s.code}-${s.cardId}`
                                        : s.code;
                                      return optionValue === selectedValue;
                                    });
                                    if (selectedSetDetail) {
                                      const newAvailableInFoil = selectedSetDetail.availableInFoil ?? card.availableInFoil;
                                      const newAvailableInNonFoil = selectedSetDetail.availableInNonFoil ?? card.availableInNonFoil;

                                      const updates: Partial<TradeCard> = {
                                        setCode: selectedSetDetail.code,
                                        set: selectedSetDetail.name,
                                        availableInFoil: newAvailableInFoil,
                                        availableInNonFoil: newAvailableInNonFoil
                                      };

                                      if (selectedSetDetail.cardId) {
                                        updates.cardId = selectedSetDetail.cardId;
                                      }

                                      if (selectedSetDetail.imageUrl) {
                                        updates.image = selectedSetDetail.imageUrl;
                                        updates.imageSmall = selectedSetDetail.imageUrl
                                          ?.replace('/large/', '/small/')
                                          ?.replace('/normal/', '/small/');
                                      }

                                      // Fetch per-set availableLanguages via getCardById
                                      // The backend scopes availableLanguages by oracleId + setCode + collectorNumber,
                                      // so fetching by the new set's scryfallId returns only languages for that set
                                      const selectedScryfallId = selectedSetDetail.scryfallId;
                                      if (selectedScryfallId) {
                                        try {
                                          const cardData = await triggerGetCard(selectedScryfallId).unwrap();
                                          // Backend returns flat response — availableLanguages at top level
                                          const perSetLanguages = (cardData as any)?.availableLanguages as
                                            Array<{ lang: string; scryfallId: string; setCode?: string }> | undefined;
                                          if (perSetLanguages && perSetLanguages.length > 0) {
                                            updates.availableLanguages = perSetLanguages;
                                            updates.scryfallId = selectedScryfallId;

                                            // Reset language if current language not available in new set
                                            const currentLangAvailable = perSetLanguages.some(
                                              l => l.lang === card.language
                                            );
                                            if (!currentLangAvailable) {
                                              const enVariant = perSetLanguages.find(l => l.lang === 'en');
                                              const fallbackLang = enVariant ? 'en' : perSetLanguages[0]?.lang || 'en';
                                              updates.language = fallbackLang;
                                            }
                                          }
                                        } catch {
                                          // On failure, keep existing availableLanguages
                                        }
                                      }

                                      const userAcceptsFoil = card.acceptFoil ?? true;
                                      const userAcceptsNonFoil = card.acceptNonFoil ?? true;
                                      const canOfferFoil = newAvailableInFoil && userAcceptsFoil;
                                      const canOfferNonFoil = newAvailableInNonFoil && userAcceptsNonFoil;

                                      if (card.foil && !canOfferFoil && canOfferNonFoil) {
                                        updates.foil = false;
                                      } else if (!card.foil && !canOfferNonFoil && canOfferFoil) {
                                        updates.foil = true;
                                      }

                                      updateCard(card.id, updates);
                                    }
                                  }}
                                  className="set-select"
                                >
                                  {[...setOptions]
                                    .sort((a, b) => {
                                      const nameCompare = a.name.localeCompare(b.name);
                                      if (nameCompare !== 0) return nameCompare;
                                      const aNum = parseInt(a.collectorNumber || '0', 10);
                                      const bNum = parseInt(b.collectorNumber || '0', 10);
                                      return aNum - bNum;
                                    })
                                    .map(setDetail => {
                                      const uniqueValue = setDetail.cardId
                                        ? `${setDetail.code}-${setDetail.cardId}`
                                        : setDetail.code;
                                      const displayText = setDetail.collectorNumber
                                        ? `${setDetail.name} #${setDetail.collectorNumber}`
                                        : setDetail.name;
                                      return (
                                        <option key={uniqueValue} value={uniqueValue}>
                                          {displayText}
                                        </option>
                                      );
                                    })}
                                </select>
                              );
                            } else {
                              const singleDetail = card.acceptableSetDetails?.[0];
                              const collectorNum = singleDetail?.collectorNumber;
                              return <span>{card.set}{collectorNum && <span style={{ color: '#888' }}> #{collectorNum}</span>}</span>;
                            }
                          })()}
                        </div>

                        <div className="col-foil">
                          {(() => {
                            const physicallyAvailableInFoil = card.availableInFoil ?? false;
                            const physicallyAvailableInNonFoil = card.availableInNonFoil ?? true;
                            const userAcceptsFoil = card.acceptFoil ?? true;
                            const userAcceptsNonFoil = card.acceptNonFoil ?? true;
                            const canOfferFoil = physicallyAvailableInFoil && userAcceptsFoil;
                            const canOfferNonFoil = physicallyAvailableInNonFoil && userAcceptsNonFoil;

                            return canOfferFoil && canOfferNonFoil ? (
                              <select
                                value={card.foil ? 'foil' : 'non-foil'}
                                onChange={(e) => updateCard(card.id, { foil: e.target.value === 'foil' })}
                                className="foil-select"
                              >
                                <option value="non-foil">Non-Foil</option>
                                <option value="foil">Foil</option>
                              </select>
                            ) : (
                              <span className="foil-text">
                                {canOfferFoil ? 'Foil' : 'Non-Foil'}
                              </span>
                            );
                          })()}
                        </div>

                        <div className="col-condition">
                          {(() => {
                            const allConditions = [
                              { value: 'NEAR_MINT', label: 'NM' },
                              { value: 'SLIGHTLY_PLAYED', label: 'LP' },
                              { value: 'MODERATELY_PLAYED', label: 'MP' },
                            ];

                            const acceptableConditions = card.acceptableConditions ||
                              allConditions.map(c => c.value);
                            const availableOptions = allConditions.filter(c =>
                              acceptableConditions.includes(c.value)
                            );

                            if (availableOptions.length > 1) {
                              return (
                                <select
                                  value={card.condition}
                                  onChange={(e) => updateCard(card.id, { condition: e.target.value })}
                                  className="condition-select"
                                >
                                  {availableOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              );
                            } else {
                              const singleCondition = availableOptions[0] || allConditions[0];
                              return <span className="condition-text">{singleCondition.label}</span>;
                            }
                          })()}
                        </div>

                        <div className="col-language">
                          {(() => {
                            const langDisplayMap: Record<string, string> = { en: 'EN', fr: 'FR', de: 'DE', es: 'ES', it: 'IT', ja: 'JP' };
                            const allLangs = [
                              { value: 'en', label: 'EN' }, { value: 'fr', label: 'FR' },
                              { value: 'de', label: 'DE' }, { value: 'es', label: 'ES' },
                              { value: 'it', label: 'IT' }, { value: 'ja', label: 'JP' },
                            ];

                            const languageOptions = card.acceptableLanguages?.length
                              ? (card.availableLanguages
                                  ?.filter(l => card.acceptableLanguages!.includes(l.lang))
                                  .map(l => ({ value: l.lang, label: langDisplayMap[l.lang] || l.lang.toUpperCase() }))
                                ) || allLangs.filter(l => card.acceptableLanguages!.includes(l.value))
                              : card.availableLanguages?.map(l => ({ value: l.lang, label: langDisplayMap[l.lang] || l.lang.toUpperCase() }))
                                || allLangs;

                            return (
                              <select
                                value={card.language}
                                onChange={(e) => updateCard(card.id, { language: e.target.value })}
                                className="language-select"
                              >
                                {languageOptions.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            );
                          })()}
                        </div>

                        {(() => {
                          const youOwnQty = haveListQuantityMap?.get(card.name) ?? 0;
                          const pkgQty = packageQtyByName.get(card.name) ?? 0;
                          const isWarning = pkgQty > youOwnQty;
                          return (
                            <div className={`col-you-own${isWarning ? ' col-you-own--warning' : ''}`}>
                              {youOwnQty}
                            </div>
                          );
                        })()}

                        <div className="col-value">
                          <Points points={card.value} type="small" />
                        </div>

                        <div className="col-remove">
                          <img
                            src={AssetUrls.icons.dropdowns.close}
                            alt="Remove"
                            className="remove-icon"
                            onClick={() => removeRow(card.id)}
                          />
                        </div>
                      </div>
                    ))}
                    {rows.length < firstCard.maxQty && (
                      <div className="add-copy-row">
                        <button
                          className="add-copy-btn"
                          onClick={() => addRow(wantListId)}
                        >
                          + Add copy ({rows.length}/{firstCard.maxQty})
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <MagicButton
            label="Cancel"
            color="dark-grey"
            onPress={onClose}
            isDisabled={isLoading}
          />
          <MagicButton
            label={isLoading ? "Initiating..." : "Initiate Trade"}
            color="purple"
            onPress={handleInitiateTrade}
            isDisabled={editableCards.length === 0 || isLoading || totalValue <= 0 || isCalculatingPrice}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}